// ---------------------------------------------------------------------------
// Núcleo de cálculo financeiro (PURO, sem React/Supabase) — usado tanto pelos
// hooks/telas quanto pelos testes. Fonte única de verdade para saldos, fatura de
// cartão e conversão de moeda, para que o app e os testes nunca divirjam.
// ---------------------------------------------------------------------------

// Mapa moeda -> taxa para a moeda base (base -> base = 1). Ex.: BRL -> 1.467.
export type Rates = Map<string, number> | undefined

export interface FinanceTx {
  account_id: string
  kind: string // 'income' | 'expense' | 'transfer'
  amount: number
  currency: string
  amount_account?: number | null
  status?: string // 'paid' | 'pending'
  date?: string // ISO yyyy-mm-dd
  transfer_group_id?: string | null
  statement_month?: string | null // cartão: mês da FATURA (dia 01). Ver faturaEndISO.
}

// Converte um valor de `from` para `to` via a base (rates: X -> base).
// Mesma moeda: valor exato. Sem taxa disponível: null.
export function convertBetween(amount: number, from: string, to: string, rates: Rates): number | null {
  if (from === to) return amount
  if (!rates) return null
  const rf = rates.get(from)
  const rt = rates.get(to)
  // rt === 0 passaria pelo guard != null e daria Infinity/NaN (uma taxa 0 corrompida
  // envenenaria toda conversão para essa moeda) — tratamos como "sem taxa".
  if (rf == null || rt == null || rt === 0) return null
  return Math.round(((amount * rf) / rt) * 100) / 100
}

// Divide um total em N parcelas na unidade de CENTAVOS, distribuindo o resto: as
// primeiras `rem` parcelas ganham 1 centavo a mais, de modo que a soma seja EXATA
// (1000 em 3 = [333.34, 333.33, 333.33]). Evita o centavo perdido do parcelamento.
export function splitCents(total: number, n: number): number[] {
  if (n <= 0) return []
  const cents = Math.round((Number(total) || 0) * 100)
  const per = Math.floor(cents / n)
  const rem = cents - per * n
  return Array.from({ length: n }, (_, i) => (per + (i < rem ? 1 : 0)) / 100)
}

// Valor de uma transação NA MOEDA DA CONTA. Preferência:
//  1) mesma moeda -> amount exato
//  2) amount_account informado -> valor fixado pelo usuário
//  3) conversão pela taxa atual
export function amountInAccount(
  t: Pick<FinanceTx, 'amount' | 'currency' | 'amount_account'>,
  accountCurrency: string,
  rates: Rates,
): number {
  if (t.currency === accountCurrency) return Number(t.amount)
  if (t.amount_account != null) return Number(t.amount_account)
  return convertBetween(Number(t.amount), t.currency, accountCurrency, rates) ?? Number(t.amount)
}

// Efeito no saldo (na moeda da conta): income soma, expense subtrai, resto 0.
// Transferências são income/expense com transfer_group_id -> contam no saldo.
export function signedInAccount(t: FinanceTx, accountCurrency: string, rates: Rates): number {
  const v = amountInAccount(t, accountCurrency, rates)
  if (t.kind === 'income') return v
  if (t.kind === 'expense') return -v
  return 0
}

// Saldo ATUAL de uma conta (na moeda dela): saldo inicial + Σ dos lançamentos
// PAGOS (qualquer data). Transferências incluídas. Cartão: negativo = dívida.
export function accountBalance(
  account: { initial_balance: number; currency: string },
  txs: FinanceTx[],
  rates: Rates,
  opts?: { onlyPaid?: boolean; untilISO?: string },
): number {
  const onlyPaid = opts?.onlyPaid ?? true
  let balance = Number(account.initial_balance)
  for (const t of txs) {
    if (onlyPaid && t.status !== 'paid') continue
    if (opts?.untilISO && t.date && t.date > opts.untilISO) continue
    balance += signedInAccount(t, account.currency, rates)
  }
  return balance
}

// DUAS RÉGUAS, de propósito — não unifique sem ler isto:
//
//  • por DATA (padrão): "quanto eu devo". Uma compra vira dívida no instante em
//    que é feita. É o que alimenta dashboard, orçamento, posição e patrimônio.
//  • por FATURA (byStatement): "quanto vem nesta conta". Uma compra feita depois
//    do fechamento já é dívida, mas só é COBRADA na fatura seguinte. É o que a
//    tela da fatura mostra e o que "Pagar fatura" liquida.
//
// Enquanto nenhum cartão tiver dia de fechamento, as duas dão o MESMO número.
export interface FaturaOpts {
  byStatement?: boolean
}

// Data efetiva de um lançamento na régua da FATURA: se ele foi empurrado para
// outra fatura, vale o último dia do mês dela; senão, a própria data.
//
// Esta é também a DATA DE CAIXA de qualquer lançamento: o dinheiro sai quando a
// fatura é paga, não quando a compra é feita. Sem `statement_month` ela é a
// própria data — que é como TODO o histórico importado funciona (os lançamentos
// de cartão vieram datados no dia do vencimento). Por isso trocar a régua não
// mexe em nenhum número existente: para 2.561 dos 2.562 lançamentos, é identidade.
export function faturaEndISO(t: Pick<FinanceTx, 'date' | 'statement_month'>): string {
  const sm = t.statement_month
  if (!sm) return t.date ?? ''
  const [y, m] = sm.split('-').map(Number)
  if (!y || !m) return t.date ?? ''
  return endOfMonthISO(y, m - 1)
}

// Mês de CAIXA ('YYYY-MM') — em que mês este lançamento tira/põe dinheiro.
export function faturaMonthOf(t: Pick<FinanceTx, 'date' | 'statement_month'>): string {
  return (t.statement_month ?? t.date ?? '').slice(0, 7)
}

function cutoffOf(t: FinanceTx, opts?: FaturaOpts): string | undefined {
  return opts?.byStatement ? faturaEndISO(t) : t.date
}

// Dívida da FATURA de um cartão até o fim de um mês (na moeda do cartão):
// saldo de abertura + TODOS os lançamentos (qualquer status) até o fim do mês.
// Positivo = dívida em aberto (0 se quitada / crédito).
export function cardFaturaDebt(
  card: { initial_balance: number; currency: string },
  txs: FinanceTx[],
  endOfMonthISO: string,
  rates: Rates,
  opts?: FaturaOpts,
): number {
  return Math.max(cardFaturaSigned(card, txs, endOfMonthISO, rates, opts), 0)
}

// Fatura do mês COM SINAL: positivo = dívida, negativo = CRÉDITO (você pagou a
// mais, ou tem estorno a favor). `cardFaturaDebt` corta o crédito em zero — o
// que é certo para EXIBIR uma fatura, mas errado para somar saldo/projeção:
// crédito no cartão é dinheiro seu e não pode simplesmente sumir.
export function cardFaturaSigned(
  card: { initial_balance: number; currency: string },
  txs: FinanceTx[],
  endOfMonthISO: string,
  rates: Rates,
  opts?: FaturaOpts,
): number {
  let closing = Number(card.initial_balance)
  for (const t of txs) {
    const cut = cutoffOf(t, opts)
    if (cut && cut > endOfMonthISO) continue
    closing += signedInAccount(t, card.currency, rates)
  }
  return -closing
}

// Dívida TOTAL de um cartão (na moeda do cartão): abertura + TODOS os lançamentos
// (qualquer status, QUALQUER data — inclui compras futuras/parcelas já lançadas).
// Usada no PATRIMÔNIO (dívida real total). Positivo = dívida; 0 se crédito.
export function cardTotalDebt(
  card: { initial_balance: number; currency: string },
  txs: FinanceTx[],
  rates: Rates,
): number {
  let closing = Number(card.initial_balance)
  for (const t of txs) closing += signedInAccount(t, card.currency, rates)
  return Math.max(-closing, 0)
}

// Fim do mês (yyyy-mm-dd) para um ano/mês0 (0-11).
export function endOfMonthISO(year: number, month0: number): string {
  const d = new Date(year, month0 + 1, 0)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// PATRIMÔNIO (net worth) — agregação consolidada na base. Recebe os saldos já
// calculados por conta + itens manuais. Cartão = passivo (valor devido = -saldo).
// ---------------------------------------------------------------------------
export interface NwAccount {
  type: string // 'cartao' vira passivo; resto é ativo
  currency: string
  balance: number // saldo na moeda da conta (cartão: negativo = dívida)
}
export interface NwItem {
  kind: 'asset' | 'liability'
  value: number
  currency: string
}
export function netWorthTotals(
  accounts: NwAccount[],
  items: NwItem[],
  baseCurrency: string,
  rates: Rates,
): { totalAssets: number; totalLiabilities: number; netWorth: number; anyMissing: boolean } {
  let totalAssets = 0
  let totalLiabilities = 0
  let anyMissing = false
  const toBase = (v: number, c: string) => convertBetween(v, c, baseCurrency, rates)
  for (const a of accounts) {
    if (a.type === 'cartao') {
      // dívida (saldo negativo) = passivo; crédito (saldo positivo) = ativo.
      if (a.balance < -0.005) {
        const conv = toBase(-a.balance, a.currency)
        if (conv == null) anyMissing = true
        else totalLiabilities += conv
      } else if (a.balance > 0.005) {
        const conv = toBase(a.balance, a.currency)
        if (conv == null) anyMissing = true
        else totalAssets += conv
      }
    } else {
      const conv = toBase(a.balance, a.currency)
      if (conv == null) anyMissing = true
      else totalAssets += conv
    }
  }
  for (const it of items) {
    const conv = toBase(it.value, it.currency)
    if (conv == null) anyMissing = true
    else if (it.kind === 'asset') totalAssets += conv
    else totalLiabilities += conv
  }
  return { totalAssets, totalLiabilities, netWorth: totalAssets - totalLiabilities, anyMissing }
}

// ---------------------------------------------------------------------------
// DASHBOARD — projeção do saldo no fim do mês. Tudo já consolidado na base.
//   saldo previsto = contas + a receber − dívidas do mês
//   dívidas do mês = fatura EM ABERTO dos cartões + pendentes fora de cartão +
//                    recorrentes do mês ainda não lançados
//   a receber      = receitas pendentes lançadas + recorrentes de receita
// ---------------------------------------------------------------------------
export interface DashInput {
  saldoContas: number
  cardFaturaAberta: number // Σ fatura em aberto dos cartões (líquida de pagamento)
  pendentesNaoCartao: number
  recorrentesDespesa: number
  receitasLancadas: number
  recorrentesReceita: number
}
export function dashboardProjection(i: DashInput): {
  dividasMes: number
  receitasAReceber: number
  saldoLiquido: number
} {
  const dividasMes = i.cardFaturaAberta + i.pendentesNaoCartao + i.recorrentesDespesa
  const receitasAReceber = i.receitasLancadas + i.recorrentesReceita
  return { dividasMes, receitasAReceber, saldoLiquido: i.saldoContas + receitasAReceber - dividasMes }
}
